import * as React from 'react'
import styled from 'styled-components'
import { FormikHelpers } from 'formik'

import { Button, TextBody } from '@ally/metronome-ui'
import { Forms } from '@ally/forms'
import {
  EnrollVerifyCustomerInfoProps,
  EnrollVerifyCustomerPayloadUnion,
} from '@ally/transmitigator'
import { LoginHeading } from '@ally/ally-login'
import { toCamelCase } from '@ally/utilitarian'

import { useTransmit } from '../../TransmitContext'
import { VerifyCustomerInfoForm } from './VerifyCustomerInfoForm'
import { VerifyCustomerAutoForm } from './VerifyCustomerAutoForm'
import { FeatureFlag } from '../../Utils'
import { useFlagContextValue } from '../../FlagContext'
import { useAppState } from '../../AppStateProvider'

const StyledCopy = styled(TextBody)`
  ${({ theme: { space } }): string => `
    &:last-of-type {
      margin-bottom: ${space.md};
    }
  `}
`

interface VerifyCustomerInfoSubmitProps {
  lastName?: string
  dateOfBirth: string
  ssn: string
  isAutoBusinessUser: boolean
}

interface VerifyCustomerAutoSubmitProps {
  tin: string
  accountNumber: string
  vin: string
  isAutoBusinessUser: boolean
}

type VerifyCustomerSubmitUnionProps =
  | VerifyCustomerInfoSubmitProps
  | VerifyCustomerAutoSubmitProps

const VerifyCustomerInfoFormsWrapper: React.FC<EnrollVerifyCustomerInfoProps> = ({
  onComplete,
  status,
  error,
}) => {
  const formId = 'enroll-customer-form'
  const initialInfoFormValues = {
    lastName: '',
    dateOfBirth: '',
    ssn: '',
    isAutoBusinessUser: false,
  }
  const initialAutoFormValues = {
    tin: '',
    accountNumber: '',
    vin: '',
    isAutoBusinessUser: true,
  }

  const { hostServices } = useTransmit()
  const { variation } = useFlagContextValue()

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Enroll:Create Login',
    })
  }, [])

  const [showAutoNGAForm, setShowAutoNGAForm] = React.useState<boolean>(false)
  const [initialFormValues, setInitialFormValues] = React.useState<
    VerifyCustomerSubmitUnionProps
  >(initialInfoFormValues)

  const isAutoNGAEnabled: boolean = variation(FeatureFlag.AutoNGA, false)
  const isLastNameRemovalOLEEnabled: boolean = variation(
    FeatureFlag.LastNameRemovalOLE,
    false,
  )

  const handleSubmit = (
    formValues: VerifyCustomerSubmitUnionProps,
    { setSubmitting, setStatus }: FormikHelpers<VerifyCustomerSubmitUnionProps>,
  ): void => {
    if (!showAutoNGAForm) {
      const {
        lastName,
        dateOfBirth,
        ssn,
        isAutoBusinessUser,
      } = formValues as VerifyCustomerInfoSubmitProps
      const basePayload = {
        dateOfBirth,
        taxID: ssn,
        isAutoBusinessUser, // TODO: handle Auto Business logic
      } as EnrollVerifyCustomerPayloadUnion
      const payload = isLastNameRemovalOLEEnabled
        ? basePayload
        : ({ ...basePayload, lastName } as EnrollVerifyCustomerPayloadUnion)
      onComplete(payload)
    }
    if (showAutoNGAForm) {
      const {
        tin,
        accountNumber,
        vin,
        isAutoBusinessUser,
      } = formValues as VerifyCustomerAutoSubmitProps
      onComplete({
        tin,
        accountNumber,
        vin,
        isAutoBusinessUser,
      } as EnrollVerifyCustomerPayloadUnion)
    }

    // Since onComplete does not return a promise, we need to immediately reset the
    // isSubmitting flag in order to be able to resubmit the form in case of error
    setSubmitting(false)
    setStatus(undefined) // reset any possible error stored in formiks status
  }

  const linkText = showAutoNGAForm
    ? "Don't have an auto business account?"
    : 'Have an auto business account?'

  const handleLinkClick = (): void => {
    const formValuePayload = showAutoNGAForm
      ? initialInfoFormValues
      : initialAutoFormValues
    setInitialFormValues(formValuePayload)
    setShowAutoNGAForm(!showAutoNGAForm)
  }
  const { isCreditCard } = useAppState()

  const headingTitle = isCreditCard
    ? 'Set up Your New Online Profile'
    : 'Set Up Online Access'
  const subCopyOne = isCreditCard
    ? 'Create your new profile and manage your credit card account with our enhanced online experience.'
    : "First, let's find you. Make sure this information exactly matches the info on your account."
  const subCopyTwo = isCreditCard
    ? 'The information you provide here should exactly match the information on your account. '
    : 'Each account owner should create their own login credentials.'

  return (
    <>
      <LoginHeading title={headingTitle} />
      <StyledCopy tag="p" size="sm" data-testid="enroll-info-copy">
        {subCopyOne}
        <br />
        {subCopyTwo}
      </StyledCopy>
      {isAutoNGAEnabled && (
        <Button
          mb="lg"
          text={linkText}
          id="toggle-verify-customer-form-link"
          onClick={handleLinkClick}
          variant="link"
          allytmln={toCamelCase(linkText)}
        />
      )}
      <Forms
        enableReinitialize
        formId={formId}
        initialValues={initialFormValues}
        onSubmit={handleSubmit}
      >
        {showAutoNGAForm ? (
          <VerifyCustomerAutoForm
            formId={formId}
            error={error}
            status={status}
          />
        ) : (
          <VerifyCustomerInfoForm
            formId={formId}
            error={error}
            status={status}
          />
        )}
      </Forms>
    </>
  )
}

export const VerifyCustomerInfo: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'VerifyCustomerInfo') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <VerifyCustomerInfoFormsWrapper {...activeScreen.payload} />
}
